import type { PlaceholderValues, Rating } from "@homeinstead/schemas";
import type { ComponentType } from "react";
import type { RenderableFormItem } from "./form-types";
import type { HeroVariantStyles } from "./hero-variant-styles";

import { BlockDebugDev } from "~www/components/PageBuilder/BlockDebugDev";
import { ContentGridFormItem } from "./ContentGridFormItem";
import { LeadFormButtonInput } from "./LeadFormButtonInput";
import { LeadFormPhoneLinkCtaFormButton } from "./LeadFormPhoneLinkCtaFormButton";
import { LeadFormRichTextBlockFormItem } from "./LeadFormRichTextBlockFormItem";
import { MultiSelectInput } from "./MultiSelectInput";
import { PrivacyDisclaimerFormItem } from "./PrivacyDisclaimerFormItem";
import { RadioInput } from "./RadioInput";
import { ReviewStarsFormItem } from "./ReviewStarsFormItem";
import { StandardInputFormItem } from "./StandardInputFormItem";

export type { RenderableFormItem as StepItem, StepInput } from "./form-types";

export interface StepInputRendererProps {
  input: RenderableFormItem;
  onContinue: () => void;
  /**
   * Called instead of onContinue when a formButton has validatesStep: true.
   * Runs step validation before allowing the step to advance.
   */
  onValidateAndContinue?: () => void;
  isSubmitting?: boolean;
  /**
   * When true, multiSelectInput suppresses its own internal Continue button.
   * Used when a sibling formButton in the same step handles step advancement,
   * preventing duplicate Continue buttons from rendering.
   */
  hideContinueButton?: boolean;
  /**
   * Called by radio inputs to report their selected value to the parent so it
   * can be persisted as a form-level hidden input across step unmounts.
   */
  onFieldChange?: (name: string, value: string) => void;
  onPostalLocationChange?: (values: {
    postalCode: string;
    location: string;
  }) => void;
  errors?: Record<string, string[]>;
  placeholderValues?: PlaceholderValues;
  phoneNumber?: string;
  officeRating?: Omit<Rating, "_type">;
  /** Step id from Sanity, forwarded to button inputs for analytics. */
  stepId?: string;
  /**
   * Analytics step name for the "Lead Form Step" event — resolved by the parent
   * as step.metricsLabel ?? step.id. Forwarded to radio, multi-select, and
   * button inputs so they can attribute their selection to the correct step.
   */
  stepName?: string;
  /**
   * Analytics `user_selection` for the step, derived from all persisted field
   * values on the step. Used by a step-level `formButton` so the unified event
   * carries the full selection when inputs do not own their own Continue button.
   * Field-keyed object; multi-select fields are arrays.
   */
  stepUserSelection?: Record<string, string | string[]>;
  /** Hero-mode contrast styles from the step info panel Visual Variant field. */
  heroVariantStyles?: HeroVariantStyles;
}

const STEP_INPUT_COMPONENTS: Partial<
  Record<RenderableFormItem["_type"], ComponentType<StepInputRendererProps>>
> = {
  contentGrid: ContentGridFormItem,
  formButton: LeadFormButtonInput,
  multiSelectInput: MultiSelectInput,
  phoneLinkCta: LeadFormPhoneLinkCtaFormButton,
  privacyDisclaimer: PrivacyDisclaimerFormItem,
  radioInput: RadioInput,
  reviewStars: ReviewStarsFormItem,
  richTextBlock: LeadFormRichTextBlockFormItem,
  standardInput: StandardInputFormItem,
};

export function StepInputRenderer({
  input,
  onContinue,
  onValidateAndContinue,
  isSubmitting,
  onFieldChange,
  onPostalLocationChange,
  errors,
  placeholderValues,
  phoneNumber,
  officeRating,
  stepId,
  stepName,
  stepUserSelection,
  hideContinueButton,
  heroVariantStyles,
}: StepInputRendererProps) {
  const Component = STEP_INPUT_COMPONENTS[input._type];

  if (!Component) {
    return <BlockDebugDev blockType={input._type} />;
  }

  return (
    <Component
      errors={errors}
      heroVariantStyles={heroVariantStyles}
      hideContinueButton={
        hideContinueButton && input._type === "multiSelectInput"
          ? true
          : undefined
      }
      input={input}
      isSubmitting={isSubmitting}
      officeRating={officeRating}
      onContinue={onContinue}
      onFieldChange={onFieldChange}
      onPostalLocationChange={onPostalLocationChange}
      onValidateAndContinue={onValidateAndContinue}
      phoneNumber={phoneNumber}
      placeholderValues={placeholderValues}
      stepId={stepId}
      stepName={stepName}
      stepUserSelection={stepUserSelection}
    />
  );
}
